import { useState, useCallback, useEffect } from "react";
import { MapPin, X, ChevronLeft, ChevronRight } from "lucide-react";
import useEmblaCarousel from "embla-carousel-react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import praxiszimmer from "@/assets/praxiszimmer.jpg.asset.json";
import praxiszimmer2 from "@/assets/praxiszimmer2.jpg.asset.json";
import aerztezentrum from "@/assets/aerztezentrum-west.png.asset.json";
import grosserRaum from "@/assets/praxis-grosser-raum.webp.asset.json";
import vorraum from "@/assets/praxis-vorraum.webp.asset.json";
import wartezimmer from "@/assets/praxis-wartezimmer.webp.asset.json";
import therapieraum from "@/assets/praxis-therapieraum.webp.asset.json";
import azWestMatte from "@/assets/praxis-az-west-matte.webp.asset.json";
import { useT } from "@/i18n";

const images = [
  {
    src: aerztezentrum.url,
    alt: "Ärztezentrum West in Wien – Eingangsbereich des Gebäudes von außen",
    altEn: "Ärztezentrum West medical centre in Vienna – exterior entrance area",
    caption: "Ärztezentrum West – Hier finden Sie meine Praxis",
    captionEn: "Ärztezentrum West – where you will find my practice",
  },
  {
    src: azWestMatte.url,
    alt: "Eingangsmatte mit Schriftzug Ärztezentrum West im Foyer",
    altEn: "Entrance mat reading Ärztezentrum West in the foyer",
    caption: "Willkommen im Ärztezentrum West",
    captionEn: "Welcome to Ärztezentrum West",
  },
  {
    src: therapieraum.url,
    alt: "Heller Vorraum der Praxis mit Sitzbank und Informationsmaterial",
    altEn: "Bright entrance hall of the practice with bench and information material",
    caption: "Der Vorraum – Ihr erster Eindruck beim Ankommen",
    captionEn: "The entrance hall – your first impression on arrival",
  },
  {
    src: wartezimmer.url,
    alt: "Wartezimmer mit zwei Sesseln, kleinem Tisch und Dachfenster",
    altEn: "Waiting room with two armchairs, a small table and a skylight",
    caption: "Das Wartezimmer – ruhig und gemütlich",
    captionEn: "The waiting room – quiet and comfortable",
  },
  {
    src: grosserRaum.url,
    alt: "Großer Praxisraum mit Sesseln, rundem Teppich und großem Fenster",
    altEn: "Large consulting room with armchairs, a round rug and a large window",
    caption: "Der große Praxisraum – Platz für gute Gespräche",
    captionEn: "The large consulting room – space for good conversations",
  },
  {
    src: vorraum.url,
    alt: "Therapiesitzungsraum mit Tisch, Fachbüchern und viel Tageslicht",
    altEn: "Therapy room with desk, specialist books and plenty of daylight",
    caption: "Der Therapieraum – hier findet die Sitzung statt",
    captionEn: "The therapy room – where the sessions take place",
  },

  {
    src: praxiszimmer.url,
    alt: "Praxisraum mit hellem Beratungstisch und Sitzgelegenheiten",
    altEn: "Consulting room with a bright consultation table and seating",
    caption: "Der Beratungsraum – ruhig und persönlich",
    captionEn: "The consultation room – calm and personal",
  },
  {
    src: praxiszimmer2.url,
    alt: "Heller Praxisraum mit Fenster, Liege und Arbeitsplatz",
    altEn: "Bright practice room with window, treatment couch and workspace",
    caption: "Lichtdurchflutet und in angenehmer Atmosphäre",
    captionEn: "Light-filled, with a pleasant atmosphere",
  },
];


const PraxisGalerie = () => {
  const t = useT(
    {
      en: false,
      eyebrow: "Einblicke",
      heading: "Meine Praxis",
      intro:
        "Damit Sie schon vorab wissen, wo Sie ankommen: ein kleiner Einblick in die Räumlichkeiten und das Ärztezentrum West.",
      location: "Ärztezentrum West, Wien",
      prev: "Vorheriges Bild",
      next: "Nächstes Bild",
      dot: (n: number) => `Zu Bild ${n} scrollen`,
      close: "Schließen",
    },
    {
      en: true,
      eyebrow: "Insights",
      heading: "My practice",
      intro:
        "So you know where you are arriving: a small glimpse of the rooms and of the Ärztezentrum West medical centre.",
      location: "Ärztezentrum West, Vienna",
      prev: "Previous image",
      next: "Next image",
      dot: (n: number) => `Scroll to image ${n}`,
      close: "Close",
    },
  );
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: "start" });
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on("select", onSelect);
    return () => {
      emblaApi.off("select", onSelect);
    };
  }, [emblaApi, onSelect]);

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);
  const scrollTo = useCallback((index: number) => emblaApi?.scrollTo(index), [emblaApi]);

  const openLightbox = (index: number) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const nextLightbox = (e: React.MouseEvent) => {
    e.stopPropagation();
    setLightboxIndex((prev) => (prev + 1) % images.length);
  };

  const prevLightbox = (e: React.MouseEvent) => {
    e.stopPropagation();
    setLightboxIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  return (
    <section id="praxis" className="py-12 md:py-16 bg-secondary/30">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto text-center mb-10">
          <span className="text-primary font-medium text-sm uppercase tracking-wider">
            {t.eyebrow}
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mt-2 mb-4">
            {t.heading}
          </h2>
          <p className="text-muted-foreground">{t.intro}</p>
          <p className="flex items-center justify-center gap-2 text-sm text-foreground mt-4">
            <MapPin className="w-4 h-4 text-primary" />
            {t.location}
          </p>
        </div>

        {/* Swipeable Carousel */}
        <div className="relative max-w-5xl mx-auto">
          <div className="overflow-hidden rounded-xl" ref={emblaRef}>
            <div className="flex">
              {images.map((img, index) => (
                <div
                  key={img.src}
                  className="flex-[0_0_85%] min-w-0 sm:flex-[0_0_45%] lg:flex-[0_0_33.333%] pl-4 first:pl-0"
                >
                  <figure
                    className="group overflow-hidden rounded-xl border border-border bg-card shadow-sm cursor-pointer h-full"
                    onClick={() => openLightbox(index)}
                  >
                    <div className="aspect-[4/3] overflow-hidden">
                      <img
                        src={img.src}
                        alt={t.en ? img.altEn : img.alt}
                        loading="lazy"
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <figcaption className="p-4 text-sm text-muted-foreground">
                      {t.en ? img.captionEn : img.caption}
                    </figcaption>
                  </figure>
                </div>
              ))}
            </div>
          </div>

          {/* Navigation Arrows */}
          <button
            onClick={scrollPrev}
            className="absolute left-2 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-white/90 backdrop-blur shadow-md border border-border flex items-center justify-center text-foreground hover:bg-primary hover:text-primary-foreground transition-colors"
            aria-label={t.prev}
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            onClick={scrollNext}
            className="absolute right-2 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-white/90 backdrop-blur shadow-md border border-border flex items-center justify-center text-foreground hover:bg-primary hover:text-primary-foreground transition-colors"
            aria-label={t.next}
          >
            <ChevronRight className="w-5 h-5" />
          </button>

          {/* Dots */}
          <div className="flex justify-center gap-2 mt-6">
            {images.map((img, index) => (
              <button
                key={img.src}
                onClick={() => scrollTo(index)}
                className={`w-2.5 h-2.5 rounded-full transition-colors ${
                  index === selectedIndex
                    ? "bg-primary"
                    : "bg-primary/30 hover:bg-primary/50"
                }`}
                aria-label={t.dot(index + 1)}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Lightbox */}
      <Dialog open={lightboxOpen} onOpenChange={setLightboxOpen}>
        <DialogContent className="max-w-[95vw] max-h-[95vh] w-auto p-0 border-none bg-black/90 shadow-2xl overflow-hidden [&>button]:hidden">
          <DialogTitle className="sr-only">
            {t.en ? images[lightboxIndex].captionEn : images[lightboxIndex].caption}
          </DialogTitle>
          <div className="relative flex flex-col items-center justify-center">
            <button
              onClick={() => setLightboxOpen(false)}
              className="absolute top-3 right-3 z-20 w-10 h-10 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black/80 transition-colors"
              aria-label={t.close}
            >
              <X className="w-5 h-5" />
            </button>

            <button
              onClick={prevLightbox}
              className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 md:w-12 md:h-12 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black/80 transition-colors"
              aria-label={t.prev}
            >
              <ChevronLeft className="w-5 h-5 md:w-6 md:h-6" />
            </button>

            <img
              src={images[lightboxIndex].src}
              alt={t.en ? images[lightboxIndex].altEn : images[lightboxIndex].alt}
              className="max-w-full max-h-[80vh] md:max-h-[85vh] object-contain rounded-lg"
            />

            <button
              onClick={nextLightbox}
              className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 md:w-12 md:h-12 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black/80 transition-colors"
              aria-label={t.next}
            >
              <ChevronRight className="w-5 h-5 md:w-6 md:h-6" />
            </button>

            <p className="mt-3 text-center text-white/90 text-sm md:text-base px-4">
              {t.en ? images[lightboxIndex].captionEn : images[lightboxIndex].caption}
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default PraxisGalerie;
